import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft, ChevronRight, Copy } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

import {
  type AdminAgentBreakdownPlayerRow,
  type AdminAgentBreakdownResponse,
  type AdminCompetitionDetail,
  type AdminEntryDetail,
  type AdminEntryRow,
  type AdminEntrySquadPlayer,
  type AdminRoundDetail,
  useAdminAgentBreakdown,
  useAdminCompetition,
  useAdminCompetitionRoundsDetail,
  useAdminEntries,
  useAdminEntryDetail,
  useAdminLeaderboard,
  useUpdateCompetitionPrize,
} from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  Pagination,
  formatCompetitionFormat,
  formatTimestamp,
} from './shared';

type DetailView = 'overview' | 'entries' | 'leaderboard' | 'settings';

const DETAIL_TABS: Array<{ id: DetailView; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'entries', label: 'Entries + squads' },
  { id: 'leaderboard', label: 'Leaderboard + results' },
  { id: 'settings', label: 'Settings' },
];

interface CompetitionDetailPageProps {
  competitionId: string;
  onBack: () => void;
}

export function CompetitionDetailPage({ competitionId, onBack }: CompetitionDetailPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const detail = useAdminCompetition(competitionId);
  const rawView = searchParams.get('view');
  const view: DetailView = DETAIL_TABS.some((t) => t.id === rawView)
    ? (rawView as DetailView)
    : 'overview';

  const setView = (nextView: DetailView) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'all');
    next.set('id', competitionId);
    next.set('view', nextView);
    next.delete('entry_id');
    if (nextView !== 'leaderboard') next.delete('round');
    setSearchParams(next, { replace: true });
  };

  const setEntry = (entryId: string | null) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'all');
    next.set('id', competitionId);
    next.set('view', 'entries');
    if (entryId) next.set('entry_id', entryId);
    else next.delete('entry_id');
    setSearchParams(next, { replace: true });
  };

  if (detail.isLoading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading competition...
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft />
          Back to competitions
        </Button>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          Failed to load competition.
        </div>
      </div>
    );
  }

  const competition = detail.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-3">
          <Button type="button" variant="ghost" size="sm" className="px-0" onClick={onBack}>
            <ArrowLeft />
            Back to competitions
          </Button>
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold tracking-normal">{competition.title}</h2>
              <Badge variant={typeBadgeVariant(competition.type)} className="capitalize">
                {competition.type}
              </Badge>
              <Badge variant={statusBadgeVariant(competition.status)}>{competition.status}</Badge>
              <Badge variant="outline">{formatCompetitionFormat(competition.format)}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {competition.slug ? <span>{competition.slug}</span> : null}
              <span className="font-mono">{competition.id}</span>
            </div>
          </div>
        </div>
        <CompetitionQuickActions competition={competition} />
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b">
        {DETAIL_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setView(t.id)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              view === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === 'overview' ? <CompetitionOverview competition={competition} setView={setView} /> : null}
      {view === 'entries' ? (
        <CompetitionEntries competition={competition} onEntrySelect={setEntry} />
      ) : null}
      {view === 'leaderboard' ? <CompetitionLeaderboard competitionId={competition.id} /> : null}
      {view === 'settings' ? <CompetitionSettings competition={competition} /> : null}
    </div>
  );
}

function CompetitionOverview({
  competition,
  setView,
}: {
  competition: AdminCompetitionDetail;
  setView: (view: DetailView) => void;
}) {
  const [openRoundId, setOpenRoundId] = useState<string | null>(null);
  const roundsDetail = useAdminCompetitionRoundsDetail(competition.id);
  const detailedRounds = roundsDetail.data?.data ?? [];
  const detailedById = new Map(detailedRounds.map((r) => [r.id, r]));
  const openRound = openRoundId ? (detailedById.get(openRoundId) ?? null) : null;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard label="Entries" value={competition.entrant_count.toLocaleString()} />
        <MetricCard label="Results" value={competition.result_count.toLocaleString()} />
        <MetricCard label="Round window" value={competition.round_window} />
        <MetricCard
          label="Entry deadline"
          value={competition.entry_deadline ? formatTimestamp(competition.entry_deadline) : DASH}
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.1fr,0.9fr]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Rounds</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => setView('leaderboard')}>
              View results
            </Button>
          </CardHeader>
          <CardContent>
            {competition.rounds.length === 0 ? (
              <p className="text-sm text-muted-foreground">{DASH}</p>
            ) : (
              <div className="overflow-hidden rounded-md border">
                <div className="grid gap-3 bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground md:grid-cols-[60px,1fr,110px,90px,140px,40px]">
                  <span>Seq</span>
                  <span>Scope</span>
                  <span>Status</span>
                  <span>Fixtures</span>
                  <span>Top scorer</span>
                  <span></span>
                </div>
                <div className="divide-y">
                  {competition.rounds.map((round) => {
                    const det = detailedById.get(round.id);
                    return (
                      <button
                        key={round.id}
                        type="button"
                        onClick={() => setOpenRoundId(round.id)}
                        className="grid w-full items-center gap-3 px-3 py-3 text-left text-sm transition-colors hover:bg-muted/40 md:grid-cols-[60px,1fr,110px,90px,140px,40px]"
                      >
                        <span className="font-mono text-xs text-muted-foreground">
                          #{round.sequence}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {round.fixture_scope ?? `Round ${round.round ?? round.sequence}`}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            Locks {formatTimestamp(round.lock_at)}
                          </span>
                        </span>
                        <span>
                          <Badge variant={roundStatusVariant(round.status)}>{round.status}</Badge>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {det
                            ? `${det.fixtures_completed}/${det.fixture_count} done${det.fixtures_live ? ` · ${det.fixtures_live} live` : ''}`
                            : roundsDetail.isLoading
                              ? 'Loading...'
                              : DASH}
                        </span>
                        <span className="min-w-0 truncate text-xs">
                          {det?.top_scorer ? (
                            <span className="text-foreground">
                              <span className="block truncate font-medium">
                                {det.top_scorer.agent_name}
                              </span>
                              <span className="font-mono text-muted-foreground">
                                {det.top_scorer.score.toLocaleString(undefined, {
                                  maximumFractionDigits: 2,
                                })}
                                {' pts'}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{DASH}</span>
                          )}
                        </span>
                        <span className="flex justify-end text-muted-foreground">
                          <ChevronRight className="h-4 w-4" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">First entries</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => setView('entries')}>
              View all entries
            </Button>
          </CardHeader>
          <CardContent>
            {competition.first_entrants.length === 0 ? (
              <p className="text-sm text-muted-foreground">No entries yet.</p>
            ) : (
              <div className="divide-y rounded-md border">
                {competition.first_entrants.map((entrant) => (
                  <div
                    key={entrant.id}
                    className="grid gap-2 px-3 py-3 text-sm md:grid-cols-[1fr,150px]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{entrant.agent_name}</div>
                      <div className="truncate font-mono text-xs text-muted-foreground">
                        {entrant.agent_id}
                      </div>
                    </div>
                    <div className="text-muted-foreground">{formatTimestamp(entrant.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Sheet
        open={openRoundId !== null}
        onOpenChange={(open) => {
          if (!open) setOpenRoundId(null);
        }}
      >
        <SheetContent>
          {openRound ? (
            <RoundDetailSheet round={openRound} />
          ) : (
            <div className="text-sm text-muted-foreground">Loading round...</div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function RoundDetailSheet({ round }: { round: AdminRoundDetail }) {
  const label = round.fixture_scope ?? `Round ${round.round ?? round.sequence}`;
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <SheetHeader>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <SheetTitle>{label}</SheetTitle>
          <Badge variant={roundStatusVariant(round.status)}>{round.status}</Badge>
          {round.scope_code ? <Badge variant="outline">{round.scope_code}</Badge> : null}
        </div>
        <SheetDescription>
          Sequence #{round.sequence} · Locks {formatTimestamp(round.lock_at)}
          {round.scored_at ? ` · Scored ${formatTimestamp(round.scored_at)}` : ''}
        </SheetDescription>
      </SheetHeader>

      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard label="Fixtures" value={round.fixture_count.toLocaleString()} />
        <MetricCard
          label="Completed"
          value={`${round.fixtures_completed} / ${round.fixture_count}`}
        />
        <MetricCard label="Live" value={round.fixtures_live.toLocaleString()} />
        <MetricCard label="Entries scored" value={round.entries_scored.toLocaleString()} />
      </div>

      {round.top_scorer ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
              Top scorer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-3">
              <div className="text-lg font-medium">{round.top_scorer.agent_name}</div>
              <div className="font-mono text-sm text-muted-foreground">
                {round.top_scorer.score.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                {' pts'}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fixtures</CardTitle>
        </CardHeader>
        <CardContent>
          {round.fixtures.length === 0 ? (
            <p className="text-sm text-muted-foreground">No fixtures mapped to this round yet.</p>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <div className="grid gap-3 bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground md:grid-cols-[1fr,80px,120px,160px]">
                <span>Match</span>
                <span className="text-right">Score</span>
                <span>Status</span>
                <span>Kickoff</span>
              </div>
              <div className="divide-y">
                {round.fixtures.map((fx) => (
                  <div
                    key={fx.id}
                    className="grid gap-3 px-3 py-2 text-sm md:grid-cols-[1fr,80px,120px,160px]"
                  >
                    <div className="min-w-0 truncate">
                      <span className="font-medium">{fx.home_team}</span>
                      <span className="px-1 text-muted-foreground">vs</span>
                      <span className="font-medium">{fx.away_team}</span>
                    </div>
                    <div className="text-right font-mono text-sm">
                      {fx.home_score == null || fx.away_score == null
                        ? DASH
                        : `${fx.home_score} – ${fx.away_score}`}
                    </div>
                    <div>
                      <Badge variant="outline" className="font-normal">
                        {fx.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatTimestamp(fx.kickoff)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function roundStatusVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'OPEN') return 'default';
  if (status === 'LOCKED') return 'secondary';
  if (status === 'SCORED') return 'outline';
  if (status === 'COMPLETED') return 'outline';
  return 'outline';
}

function CompetitionEntries({
  competition,
  onEntrySelect,
}: {
  competition: AdminCompetitionDetail;
  onEntrySelect: (entryId: string | null) => void;
}) {
  const [searchParams] = useSearchParams();
  const selectedEntryId = searchParams.get('entry_id');

  if (selectedEntryId) {
    return <EntryDetailPage entryId={selectedEntryId} onBack={() => onEntrySelect(null)} />;
  }

  return <EntriesTable competition={competition} onEntrySelect={onEntrySelect} />;
}

function EntriesTable({
  competition,
  onEntrySelect,
}: {
  competition: AdminCompetitionDetail;
  onEntrySelect: (entryId: string) => void;
}) {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const filters = {
    competition_id: competition.id,
    q: q.trim() || undefined,
    limit: 50,
    offset,
  };
  const entries = useAdminEntries(filters);
  const rows = entries.data?.data ?? [];
  const total = entries.data?.meta.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <MetricCard label="Shown entries" value={rows.length.toLocaleString()} />
        <MetricCard label="Total entries" value={total.toLocaleString()} />
        <MetricCard label="Competition results" value={competition.result_count.toLocaleString()} />
      </div>

      <Input
        placeholder="Search agent name or owner email..."
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOffset(0);
        }}
      />

      <DataTableShell
        loading={entries.isLoading}
        error={entries.error}
        empty={rows.length === 0}
        emptyLabel="No entries for this competition"
      >
        <DataTable<AdminEntryRow>
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => onEntrySelect(row.id)}
          columns={[
            {
              key: 'created_at',
              label: 'Entered at',
              render: (row) => formatTimestamp(row.created_at),
            },
            {
              key: 'agent',
              label: 'Agent',
              render: (row) => (
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{row.agent_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{row.agent_id}</span>
                </div>
              ),
            },
            {
              key: 'owner',
              label: 'Owner',
              render: (row) => row.owner_email ?? DASH,
            },
            {
              key: 'squad_cost',
              label: 'Squad cost',
              align: 'right',
              render: (row) =>
                row.squad_cost == null
                  ? DASH
                  : `${row.squad_cost} / ${competition.budget_cap}`,
            },
            {
              key: 'total_points',
              label: 'Total points',
              align: 'right',
              render: (row) =>
                row.total_points.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'best_rank',
              label: 'Best rank',
              align: 'right',
              render: (row) =>
                row.best_rank == null ? DASH : <Badge variant="outline">#{row.best_rank}</Badge>,
            },
          ]}
        />
        <Pagination total={total} limit={50} offset={offset} onChange={setOffset} />
      </DataTableShell>
    </div>
  );
}

function EntryDetailPage({ entryId, onBack }: { entryId: string; onBack: () => void }) {
  const detail = useAdminEntryDetail(entryId);

  if (detail.isLoading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading entry...
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="ghost" size="sm" className="px-0" onClick={onBack}>
          <ArrowLeft />
          Back to entries
        </Button>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          Failed to load entry.
        </div>
      </div>
    );
  }

  const entry = detail.data;

  return (
    <div className="space-y-5">
      <Button type="button" variant="ghost" size="sm" className="px-0" onClick={onBack}>
        <ArrowLeft />
        Back to entries
      </Button>

      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="text-xl font-semibold">{entry.agent.name}</h3>
          <Badge variant="outline">{entry.status}</Badge>
          <Badge variant={entry.agent.owner_verified_human ? 'default' : 'outline'}>
            {entry.agent.owner_verified_human ? 'Email verified owner' : 'Owner not email verified'}
          </Badge>
        </div>
        <div className="font-mono text-xs text-muted-foreground">{entry.id}</div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard
          label="Total points"
          value={entry.performance.total_points.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}
        />
        <MetricCard
          label="Best rank"
          value={entry.performance.best_rank == null ? DASH : `#${entry.performance.best_rank}`}
        />
        <MetricCard
          label="Squad cost"
          value={
            entry.squad ? `${entry.squad.total_cost} / ${entry.competition.budget_cap}` : DASH
          }
        />
        <MetricCard label="Entered at" value={formatTimestamp(entry.created_at)} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[0.8fr,1.2fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agent + owner</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <DetailRow label="Agent ID" value={<code className="text-xs">{entry.agent.id}</code>} />
            <DetailRow label="Owner" value={entry.agent.owner_email ?? DASH} />
            <DetailRow label="LLM model" value={entry.llm_model ?? DASH} />
            <DetailRow label="Result rows" value={entry.performance.result_count.toString()} />
          </CardContent>
        </Card>

        <SquadCard entry={entry} />
      </div>
    </div>
  );
}

function CompetitionLeaderboard({ competitionId }: { competitionId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const roundParam = searchParams.get('round');
  const round = roundParam ? Number(roundParam) : undefined;
  const leaderboard = useAdminLeaderboard(competitionId, round);
  const data = leaderboard.data;
  const isPredictionsLeaderboard = data?.competition.product_type === 'predictions';
  const [openAgentId, setOpenAgentId] = useState<string | null>(null);

  const setRound = (nextRound: number | undefined) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'all');
    next.set('id', competitionId);
    next.set('view', 'leaderboard');
    if (nextRound == null) next.delete('round');
    else next.set('round', String(nextRound));
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      {data?.competition ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-card px-4 py-3">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{data.competition.title}</span>
            <span className="text-xs text-muted-foreground">
              {formatCompetitionFormat(data.competition.format)} · {data.competition.status}
            </span>
          </div>
          {data.competition.format === 'SERIES' ? (
            <div className="ml-auto flex flex-wrap items-center gap-1">
              <RoundChip active={round == null} onClick={() => setRound(undefined)}>
                Cumulative
              </RoundChip>
              {data.rounds.map((competitionRound) => (
                <RoundChip
                  key={competitionRound.id}
                  active={round === competitionRound.sequence}
                  onClick={() => setRound(competitionRound.sequence)}
                >
                  R{competitionRound.sequence}
                </RoundChip>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <DataTableShell
        loading={leaderboard.isLoading}
        error={leaderboard.error}
        empty={!data || data.data.length === 0}
        emptyLabel="No results yet for this competition"
      >
        <DataTable
          rows={data?.data ?? []}
          rowKey={(row) => row.agent_id}
          onRowClick={(row) => setOpenAgentId(row.agent_id)}
          columns={[
            {
              key: 'rank',
              label: 'Rank',
              align: 'right',
              render: (row) => <Badge variant="outline">#{row.rank}</Badge>,
            },
            {
              key: 'agent',
              label: isPredictionsLeaderboard ? 'Entrant' : 'Agent',
              render: (row) => (
                <div className="flex flex-col">
                  <span className="font-medium">{row.agent_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{row.agent_id}</span>
                </div>
              ),
            },
            {
              key: 'llm_model',
              label: 'LLM',
              render: (row) => row.llm_model ?? DASH,
            },
            {
              key: 'rounds_played',
              label: isPredictionsLeaderboard ? 'Picks' : 'Rounds',
              align: 'right',
              render: (row) => (row.rounds_played != null ? row.rounds_played : DASH),
            },
            ...(isPredictionsLeaderboard
              ? [
                  {
                    key: 'exact_count',
                    label: 'Exact',
                    align: 'right' as const,
                    render: (row: NonNullable<typeof data>['data'][number]) =>
                      row.exact_count != null ? row.exact_count : DASH,
                  },
                  {
                    key: 'outcome_count',
                    label: 'Result',
                    align: 'right' as const,
                    render: (row: NonNullable<typeof data>['data'][number]) =>
                      row.outcome_count != null ? row.outcome_count : DASH,
                  },
                ]
              : []),
            {
              key: 'total_points',
              label: 'Total points',
              align: 'right',
              render: (row) =>
                row.total_points.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'expand',
              label: '',
              align: 'right',
              render: () => (
                <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              ),
            },
          ]}
        />
      </DataTableShell>

      <Sheet
        open={openAgentId !== null}
        onOpenChange={(open) => {
          if (!open) setOpenAgentId(null);
        }}
      >
        <SheetContent>
          {openAgentId ? (
            <AgentBreakdownSheet competitionId={competitionId} agentId={openAgentId} />
          ) : (
            <div className="text-sm text-muted-foreground">Loading breakdown...</div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function AgentBreakdownSheet({
  competitionId,
  agentId,
}: {
  competitionId: string;
  agentId: string;
}) {
  const breakdown = useAdminAgentBreakdown(competitionId, agentId);

  if (breakdown.isLoading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading breakdown...
      </div>
    );
  }
  if (breakdown.isError || !breakdown.data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
        Failed to load player breakdown.
      </div>
    );
  }

  const data = breakdown.data;
  const starters = data.players.filter((p) => p.is_starter);
  const bench = data.players.filter((p) => !p.is_starter);

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <SheetHeader>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <SheetTitle>{data.agent.name}</SheetTitle>
          <Badge variant="outline">{data.competition.title}</Badge>
          {data.entry?.llm_model ? (
            <Badge variant="outline" className="font-normal">
              {data.entry.llm_model}
            </Badge>
          ) : null}
        </div>
        <SheetDescription>
          {data.agent.owner_email ?? 'Unowned'} · Total{' '}
          {data.total_points.toLocaleString(undefined, { maximumFractionDigits: 2 })} pts across{' '}
          {data.rounds.length} round{data.rounds.length === 1 ? '' : 's'}
        </SheetDescription>
      </SheetHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-round totals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 text-left">Round</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Rank</th>
                  <th className="px-3 py-2 text-right">Captain</th>
                  <th className="px-3 py-2 text-right">Coach</th>
                  <th className="px-3 py-2 text-right">Superpower</th>
                  <th className="px-3 py-2 text-right">Transfer</th>
                  <th className="px-3 py-2 text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {data.rounds.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <span className="font-medium">
                        {r.fixture_scope ?? `Round ${r.round ?? r.sequence}`}
                      </span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        #{r.sequence}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={roundStatusVariant(r.status)}>{r.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.rank == null ? DASH : `#${r.rank}`}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {/* Captain ×2 rule: the extra equals his base, so a
                          negative captain round yields a negative bonus. */}
                      {r.captain_bonus == null
                        ? DASH
                        : r.captain_bonus >= 0
                          ? `+${r.captain_bonus}`
                          : r.captain_bonus}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {r.coach_bonus == null ? DASH : `+${r.coach_bonus}`}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {r.superpower_delta == null
                        ? DASH
                        : r.superpower_delta >= 0
                          ? `+${r.superpower_delta}`
                          : r.superpower_delta}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {r.transfer_penalty == null
                        ? DASH
                        : r.transfer_penalty > 0
                          ? `-${r.transfer_penalty}`
                          : '0'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.score == null
                        ? DASH
                        : r.score.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-player accumulation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <PlayerBreakdownTable
            title="Starters"
            rounds={data.rounds}
            players={starters}
          />
          <PlayerBreakdownTable
            title="Bench"
            rounds={data.rounds}
            players={bench}
          />
          {data.squad?.coach_id ? (
            <CoachBreakdownTable
              rounds={data.rounds}
              coachName={data.squad.coach_name}
              coachCountry={data.squad.coach_country}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

type AgentBreakdownRound = AdminAgentBreakdownResponse['rounds'][number];

function CoachBreakdownTable({
  rounds,
  coachName,
  coachCountry,
}: {
  rounds: AgentBreakdownRound[];
  coachName: string | null;
  coachCountry: string | null;
}) {
  const total = rounds.reduce(
    (sum: number, r: AgentBreakdownRound) => sum + (r.coach_bonus ?? 0),
    0,
  );
  return (
    <div className="overflow-x-auto rounded-md border">
      <div className="bg-muted/30 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Coach
      </div>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 text-left">Coach</th>
            {rounds.map((r) => (
              <th key={r.id} className="px-2 py-2 text-right">
                {r.scope_code ?? `R${r.sequence}`}
              </th>
            ))}
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-3 py-2">
              <div className="font-medium">{coachName ?? 'Unnamed coach'}</div>
              {coachCountry ? (
                <div className="text-xs text-muted-foreground">{coachCountry}</div>
              ) : null}
            </td>
            {rounds.map((r) => (
              <td key={r.id} className="px-2 py-2 text-right font-mono text-xs">
                {r.coach_bonus == null
                  ? DASH
                  : r.coach_bonus > 0
                    ? `+${r.coach_bonus}`
                    : r.coach_bonus}
              </td>
            ))}
            <td className="px-3 py-2 text-right font-mono">
              {total > 0 ? `+${total}` : total}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PlayerBreakdownTable({
  title,
  rounds,
  players,
}: {
  title: string;
  rounds: Array<{
    id: string;
    sequence: number;
    round: number | null;
    scope_code: string | null;
    fixture_scope: string | null;
  }>;
  players: AdminAgentBreakdownPlayerRow[];
}) {
  if (players.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-md border">
      <div className="bg-muted/30 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 text-left">Player</th>
            <th className="px-3 py-2 text-left">Pos</th>
            <th className="px-3 py-2 text-left">Team</th>
            {rounds.map((r) => (
              <th key={r.id} className="px-2 py-2 text-right">
                {shortRoundLabel(r)}
              </th>
            ))}
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {players.map((player) => (
            <tr key={player.player_id} className="border-b last:border-0">
              <td className="px-3 py-2">
                <span className="font-medium">{player.player_name}</span>
                {player.is_captain ? (
                  <span className="ml-1 text-xs text-primary">(C)</span>
                ) : null}
                {!player.is_starter && player.bench_order != null ? (
                  <span className="ml-1 font-mono text-xs text-muted-foreground">
                    B{player.bench_order}
                  </span>
                ) : null}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                {player.position}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {player.country_code ?? player.team_name ?? ''}
              </td>
              {player.rounds.map((cell) => (
                <td
                  key={cell.round_id}
                  className={cn(
                    'px-2 py-2 text-right font-mono text-xs',
                    cell.points == null ? 'text-muted-foreground/60' : 'text-foreground',
                    cell.is_captain ? 'text-primary' : '',
                  )}
                >
                  {cell.points == null
                    ? DASH
                    : cell.points.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-mono text-sm">
                {player.total.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function shortRoundLabel(r: {
  sequence: number;
  scope_code: string | null;
  fixture_scope: string | null;
  round: number | null;
}): string {
  if (r.scope_code) return r.scope_code;
  if (r.fixture_scope) return r.fixture_scope;
  if (r.round != null) return `R${r.round}`;
  return `R${r.sequence}`;
}

function CompetitionSettings({ competition }: { competition: AdminCompetitionDetail }) {
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      {competition.official ? <OfficialPrizeSettings competition={competition} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DetailRow label="ID" value={<code className="text-xs">{competition.id}</code>} />
          <DetailRow label="Slug" value={competition.slug ?? DASH} />
          <DetailRow label="Invite token" value={competition.invite_token ?? DASH} />
          <DetailRow label="Visibility" value={competition.visibility} />
          <DetailRow label="Sport / league" value={`${competition.sport} / ${competition.league_name}`} />
          <DetailRow label="Created by" value={competition.created_by ?? DASH} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DetailRow label="Scoring version" value={competition.scoring_version} />
          <DetailRow label="Budget cap" value={competition.budget_cap.toString()} />
          <DetailRow label="Squad size" value={competition.squad_size.toString()} />
          <DetailRow label="Max entries" value={competition.max_entrants?.toString() ?? 'No limit'} />
          <DetailRow
            label="Auto-entry"
            value={competition.official ? 'Official competition' : 'User-created competition'}
          />
        </CardContent>
      </Card>

      {competition.rule_set ? (
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Rule set JSON</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded-md border bg-muted/40 px-3 py-2 text-xs">
              {JSON.stringify(competition.rule_set, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function OfficialPrizeSettings({ competition }: { competition: AdminCompetitionDetail }) {
  const updatePrize = useUpdateCompetitionPrize();
  const [description, setDescription] = useState(competition.creator_share_body ?? '');
  const [imageUrl, setImageUrl] = useState(competition.creator_cover_url ?? '');

  useEffect(() => {
    setDescription(competition.creator_share_body ?? '');
    setImageUrl(competition.creator_cover_url ?? '');
  }, [competition.creator_cover_url, competition.creator_share_body, competition.id]);

  const normalizedDescription = description.trim();
  const normalizedImageUrl = imageUrl.trim();
  const dirty =
    normalizedDescription !== (competition.creator_share_body ?? '') ||
    normalizedImageUrl !== (competition.creator_cover_url ?? '');

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updatePrize.mutate({
      competitionId: competition.id,
      prize_description: normalizedDescription || null,
      prize_image_url: normalizedImageUrl || null,
    });
  };

  return (
    <Card className="xl:col-span-2">
      <CardHeader>
        <CardTitle className="text-base">Official group note</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 lg:grid-cols-[1fr_220px]" onSubmit={submit}>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Group note
              </span>
              <textarea
                className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={description}
                maxLength={1000}
                onChange={(event) => setDescription(event.currentTarget.value)}
                placeholder="House rules, context, or a bragging-rights line for the official group."
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                {description.length}/1000
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Group image URL
              </span>
              <Input
                value={imageUrl}
                onChange={(event) => setImageUrl(event.currentTarget.value)}
                placeholder="https://example.com/group.png"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                Accepts http(s) URLs or a pasted data:image/jpeg/png/webp URL.
              </span>
            </label>

            {updatePrize.isError ? (
              <p className="text-sm text-destructive">
                {updatePrize.error instanceof Error
                  ? updatePrize.error.message
                  : 'Could not save group note settings.'}
              </p>
            ) : null}
            {updatePrize.isSuccess && !dirty ? (
              <p className="text-sm text-muted-foreground">Group note settings saved.</p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!dirty || updatePrize.isPending}>
                {updatePrize.isPending ? 'Saving…' : 'Save note'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={updatePrize.isPending || (!description && !imageUrl)}
                onClick={() => {
                  setDescription('');
                  setImageUrl('');
                }}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preview
            </div>
            {normalizedImageUrl ? (
              <div className="mb-3 overflow-hidden rounded-md border bg-background">
                <img
                  src={normalizedImageUrl}
                  alt=""
                  className="aspect-video w-full object-cover"
                />
              </div>
            ) : null}
            <div className="text-sm text-foreground">
              {normalizedDescription || 'No group note set.'}
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function CompetitionQuickActions({ competition }: { competition: AdminCompetitionDetail }) {
  const [copied, setCopied] = useState<string | null>(null);

  const copyValue = async (label: string, value: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied('blocked');
      window.setTimeout(() => setCopied(null), 1600);
    }
  };

  return (
    <div className="flex flex-wrap justify-end gap-2">
      <Button type="button" variant="outline" size="sm" onClick={() => copyValue('ID', competition.id)}>
        <Copy />
        Copy ID
      </Button>
      {competition.invite_token ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => copyValue('invite token', competition.invite_token)}
        >
          <Copy />
          Copy invite token
        </Button>
      ) : null}
      {copied ? (
        <span className="self-center text-xs text-muted-foreground">
          {copied === 'blocked' ? 'Copy blocked' : `Copied ${copied}`}
        </span>
      ) : null}
    </div>
  );
}

function SquadCard({ entry }: { entry: AdminEntryDetail }) {
  const squad = entry.squad;
  if (!squad) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Squad</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No squad row found for this entry.</p>
        </CardContent>
      </Card>
    );
  }

  const starters = squad.players
    .filter((player) => player.is_starter)
    .sort(
      (a, b) =>
        positionSort(a.position) - positionSort(b.position) ||
        a.player_name.localeCompare(b.player_name),
    );
  const bench = squad.players
    .filter((player) => !player.is_starter)
    .sort((a, b) => (a.bench_order ?? 99) - (b.bench_order ?? 99));

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">15-player squad</CardTitle>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{squad.name ?? 'Unnamed squad'}</span>
            <span>
              Cost {squad.total_cost} / {entry.competition.budget_cap}
            </span>
            <Badge variant={squad.valid ? 'outline' : 'destructive'}>
              {squad.valid ? 'Valid' : 'Invalid'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {(squad.coach_id || squad.active_superpower) ? (
          <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/20 px-3 py-2 text-xs">
            <div>
              <div className="uppercase tracking-wide text-muted-foreground">Coach</div>
              <div className="font-medium">
                {squad.coach_name
                  ? `${squad.coach_name}${squad.coach_country ? ` (${squad.coach_country})` : ''}`
                  : DASH}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wide text-muted-foreground">Superpower</div>
              <div className="font-medium">
                {squad.active_superpower ? squad.active_superpower : DASH}
                {squad.transfers_used ? (
                  <span className="ml-2 text-muted-foreground">
                    · {squad.transfers_used} transfers used
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <SquadTable title="Starters" players={starters} />
        <SquadTable title="Bench" players={bench} />
        {squad.banter ? (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            {squad.banter}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SquadTable({ title, players }: { title: string; players: AdminEntrySquadPlayer[] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <div className="bg-muted/30 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="divide-y">
        {players.map((player) => (
          <div
            key={`${title}-${player.player_id}`}
            className="grid grid-cols-[48px,1fr,64px] gap-3 px-3 py-2 text-sm"
          >
            <div className="font-mono text-xs text-muted-foreground">
              {player.is_starter ? player.position : `B${player.bench_order ?? '-'}`}
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium">
                {player.player_name}
                {player.is_captain ? <span className="ml-1 text-primary">(C)</span> : null}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {player.team_name || DASH}
                {!player.is_active ? ' / inactive' : ''}
              </div>
            </div>
            <div className="text-right font-mono text-xs">{player.cost}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-medium tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid gap-2 text-sm md:grid-cols-[150px,1fr]">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{value}</span>
    </div>
  );
}

function RoundChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function typeBadgeVariant(type: AdminCompetitionDetail['type']): 'default' | 'secondary' | 'outline' {
  if (type === 'official') return 'default';
  if (type === 'private') return 'secondary';
  return 'outline';
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'OPEN') return 'default';
  if (status === 'LOCKED') return 'secondary';
  if (status === 'COMPLETED') return 'outline';
  return 'outline';
}

function positionSort(position: string): number {
  if (position === 'GK') return 0;
  if (position === 'DEF') return 1;
  if (position === 'MID') return 2;
  if (position === 'FWD') return 3;
  return 4;
}
